import { LoadingScreen, useToast } from "@flat-ui";
import { useEffect } from "react";
import { useErrorHandlerContext } from "@context-providers/error-handler/error-handler-context";
import { faroCoreApiClient } from "@api/core-api/core-api-utils";
import { HttpUtils } from "@stellar/utils";

/**
 * Page that handles the redirect after logging of the Connect App.
 * The Connect app expects a request to the endpoint v1/users/getLoggedInUsers, the app
 * intercepts the request and gets the session ID from the request headers.
 */
export function ConnectLoginRedirect(): JSX.Element {
  const { openToast, closeToast } = useToast();
  const { handleError } = useErrorHandlerContext();

  // Make a request to the getLoggedInUser endpoint
  useEffect(() => {
    async function getLoggedInUser(): Promise<void> {
      openToast({
        key: "ConnectLoginRedirect-getLoggedInUser-loading",
        title: "Getting user information...",
        variant: "info",
      });

      try {
        // Important: The Connect app specifically listens to the `/v1/users/getLoggedInUser`
        // endpoint. If the request is removed then the login in the Connect app will break.
        // Do not remove this endpoint without consulting first with the Connect app team on
        // alternatives to change their login implementation.
        await faroCoreApiClient.users.getLoggedInUserV1();
        openToast({
          key: "ConnectLoginRedirect-getLoggedInUser-success",
          title: "User information loaded",
          variant: "success",
        });
      } catch (error) {
        // The Core API returns an error with status code 401 if the user is not authenticated
        // in this case simply show a toast message but don't log the error to Sentry
        const errorStatus = HttpUtils.getErrorStatus(error);
        const isLoggedOutError =
          errorStatus !== undefined &&
          errorStatus === HttpUtils.HTTP_ERROR_CODE.Unauthorized;
        if (isLoggedOutError) {
          openToast({
            key: "ConnectLoginRedirect-getLoggedInUser-loggedOut",
            title: "Failed to load the user info",
            message: "User is not logged in",
            variant: "error",
          });
        } else {
          handleError({
            id: `getLoggedInUser-${Date.now().toString()}`,
            title: "Failed to load the user info",
            error,
            uiType: "toast",
          });
        }
      }

      closeToast("ConnectLoginRedirect-getLoggedInUser-loading");
    }

    void getLoggedInUser();
  }, [closeToast, handleError, openToast]);

  return <LoadingScreen />;
}
